import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Product, Review, Order, Inquiry, Notice, Category, ProductPrice, Coupon } from '../types';
import { categoryApi, productApi, reviewApi, inquiryApi, orderApi, couponApi, pointApi } from '../lib/supabase-api';
import { supabase } from '../lib/supabase';
import { useSupabaseData } from './SupabaseDataContext'; // Import for data sharing
import { useAuth } from './SupabaseAuthContext'; // Auth state
import { devLog } from '../lib/dev-logger'; // Dev-only logging

interface AdminContextType {
  // Loading state
  isLoading: boolean;
  
  // Products
  products: Product[];
  addProduct: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateProduct: (productId: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  getProduct: (productId: string) => Promise<Product | undefined>;
  getProductSync: (productId: string) => Product | undefined;
  // Removed getProductsAsLegacy - using Product directly now
  
  // Product Prices (quantity-based pricing system)
  productPrices: Map<string, ProductPrice[]>; // Map keyed by product_id
  getProductPrices: (productId: string) => ProductPrice[];
  calculateVolumeDiscount: (productId: string, quantity: number) => number; // Volume discount calculation
  addProductPrice: (productId: string, priceData: Omit<ProductPrice, 'id' | 'variant_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  upsertProductPrice: (productId: string, priceData: Omit<ProductPrice, 'id' | 'variant_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  deleteProductPrice: (priceId: number) => Promise<void>;
  
  // Reviews
  reviews: Review[];
  addReview: (reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Review | undefined>;
  updateReview: (reviewId: string, updates: Partial<Review>) => void;
  updateReviewStatus: (reviewId: string, status: 'pending' | 'approved' | 'rejected') => void;
  approveReview: (reviewId: string, author?: string) => void;
  rejectReview: (reviewId: string, author?: string) => void;
  replyToReview: (reviewId: string, reply: string, author?: string) => void;
  deleteReview: (reviewId: string) => void;
  
  // Orders
  orders: Order[];
  updateOrder: (orderId: string, updates: Partial<Order>) => void;
  updateOrderStatus: (orderId: string, status: 'payment_pending' | 'payment_confirmed' | 'shipping' | 'delivered' | 'cancelled' | 'refunded' | 'trash') => void;
  updatePaymentStatus: (orderId: string, status: 'pending' | 'paid' | 'failed' | 'refunded') => void;
  addTrackingNumber: (orderId: string, trackingNumber: string, shippingCompany?: string) => void;
  deleteOrder: (orderId: string) => Promise<boolean>; // Permanent order deletion
  
  // WordPress-style trash features
  trashOrders: Order[]; // Trash order list
  moveOrderToTrash: (orderId: string) => Promise<boolean>; // Move to trash
  restoreOrderFromTrash: (orderId: string, previousStatus?: string) => Promise<boolean>; // Restore from trash
  loadTrashOrders: () => Promise<void>; // Load trash orders
  
  // Inquiries
  inquiries: Inquiry[];
  addInquiry: (inquiry: Omit<Inquiry, 'id' | 'createdAt' | 'updatedAt'>) => Promise<any>;
  updateInquiryStatus: (inquiryId: string, status: 'pending' | 'answered' | 'closed') => void;
  addInquiryResponse: (inquiryId: string, response: string) => Promise<void>;
  replyToInquiry: (inquiryId: string, response: string, author?: string) => Promise<void>;
  updateInquiry: (inquiryId: string, updates: Partial<Inquiry>) => void;
  
  
  // Notices
  notices: Notice[];
  addNotice: (notice: Notice) => void;
  updateNotice: (noticeId: string, updates: Partial<Notice>) => void;
  deleteNotice: (noticeId: string) => void;
  
  // Categories
  categories: Category[];
  flatCategories: Category[];
  addCategory: (name: string, parentId?: number) => void;
  updateCategory: (id: number, updates: Partial<Category>) => void;
  deleteCategory: (id: number) => void;
  getCategoryTree: () => Category[];
  getCategoryPath: (categoryId: number) => Category[];

  // Coupons
  coupons: Coupon[];
  createCoupon: (couponData: Omit<Coupon, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateCoupon: (couponId: string, updates: Partial<Coupon>) => Promise<void>;
  deleteCoupon: (couponId: string) => Promise<void>;
  toggleCouponStatus: (couponId: string, currentStatus: boolean) => Promise<void>;

  // Admin Authentication
  isAdminLoggedIn: boolean;
  adminUser: {
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'super_admin';
  } | null;
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  logoutAdmin: () => Promise<void>;
  
  // Stats
  getStats: () => {
    totalProducts: number;
    totalOrders: number;
    totalRevenue: number;
    pendingInquiries: number;
    pendingReviews: number;
  };
}

const AdminContext = createContext<AdminContextType | null>(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};


export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Auth state (prevents duplicate subscriptions)
  const { user, loading: authLoading } = useAuth();
  
  // Data sharing: get data from SupabaseDataContext (prevents duplicate requests)
  const { products: supabaseProducts, categories: supabaseCategories, isLoading: supabaseLoading } = useSupabaseData();
  
  // AdminContext-specific states (only those not duplicated in SupabaseDataContext)
  const [adminDataLoading, setAdminDataLoading] = useState<boolean>(true);
  
  // Combined loading state: both SupabaseData + AdminData must complete
  const isLoading = supabaseLoading || adminDataLoading;
  
  // Real-time admin permission check (based on useAuth state)
  const checkAdminStatus = async () => {
    try {
      // Supabase session check (for debugging)
      const { data: sessionData } = await supabase.auth.getSession();
      devLog.info('🔐 Supabase Session:', sessionData.session ? 'EXISTS ✅' : 'NULL ❌');
      if (sessionData.session) {
        devLog.info('   └─ User ID:', sessionData.session.user.id);
        devLog.info('   └─ Email:', sessionData.session.user.email);
      }

      if (user) {
        devLog.info('Current logged-in user:', user.email);
        
        // Check admin permission in user_profiles
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profile && ['ADMIN', 'SUPER_ADMIN'].includes(profile.grade)) {
          devLog.info('Admin permission confirmed:', profile.name, profile.grade);
          setIsAdminLoggedIn(true);
          setAdminUser({
            id: user.id,
            name: profile.name || 'Admin',
            email: user.email || '',
            role: profile.grade === 'SUPER_ADMIN' ? 'super_admin' : 'admin'
          });
        } else {
          devLog.info('No admin permission:', profile?.grade || 'profile not found');
          setIsAdminLoggedIn(false);
          setAdminUser(null);
        }
      } else {
        devLog.info('No logged-in user');
        setIsAdminLoggedIn(false);
        setAdminUser(null);
      }
    } catch (error) {
      devLog.error('Admin permission check failed:', error);
      setIsAdminLoggedIn(false);
      setAdminUser(null);
    }
  };
  const [productPrices, setProductPrices] = useState<Map<string, ProductPrice[]>>(new Map());
  const [reviews, setReviews] = useState<Review[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [trashOrders, setTrashOrders] = useState<Order[]>([]); // Trash order list
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState<boolean>(false);
  const [adminUser, setAdminUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'super_admin';
  } | null>(null);

  // Use data from SupabaseDataContext in AdminContext format
  const products = supabaseProducts;
  const categories = supabaseCategories;

  // Derive productPrices Map from nested products (default variant's prices per product family).
  // This preserves the legacy Map<productId, ProductPrice[]> shape used by AdminProducts etc.
  useEffect(() => {
    if (!supabaseProducts || supabaseProducts.length === 0) return;
    const map = new Map<string, ProductPrice[]>();
    for (const p of supabaseProducts) {
      const variants = (p as any).variants || [];
      if (variants.length === 0) continue;
      const defaultVariant = variants.find((v: any) => v.is_default) || variants[0];
      if (defaultVariant?.prices?.length) {
        map.set(p.id, defaultVariant.prices);
      }
    }
    setProductPrices(map);
    devLog.info(`✅ AdminContext: productPrices Map derived for ${map.size} products`);
  }, [supabaseProducts]);

  // Load orders from Supabase (RLS bypass via Edge Function)
  const loadOrders = async () => {
    try {
      // orderApi.getAll() calls Edge Function to get all orders.
      const transformedOrders = await orderApi.getAll();
      setOrders(transformedOrders);
      devLog.info(`✅ AdminContext: Loaded ${transformedOrders.length} active orders via Edge Function`);
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to load orders:', error);
      setOrders(getInitialOrders());
    }
  };

  // Realtime subscriptions — gated on isAdminLoggedIn so the WebSocket only
  // opens after admin auth. Lifted to component scope (from an inner closure
  // inside the init effect) to eliminate the ref-handoff race where a cached
  // session's checkAdminStatus flipped isAdminLoggedIn before initializeData
  // finished, leaving realtime permanently un-opened.
  const setupRealtimeSubscriptions = useCallback(() => {
    devLog.info('🔄 AdminContext: Setting up real-time subscriptions...');

    const ordersSubscription = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        async (payload: any) => {
          devLog.info('🔄 Real-time: Orders table changed', payload);
          setTimeout(async () => {
            try {
              const { data: supabaseOrders, error } = await supabase
                .from('orders')
                .select(`*, order_items (*)`)
                .order('created_at', { ascending: false });
              if (!error && supabaseOrders) {
                const transformedOrders = supabaseOrders.map((order: any) => ({
                  id: order.id,
                  userId: order.user_id || undefined,
                  userName: order.user_name,
                  userEmail: order.user_email,
                  orderNumber: order.order_number,
                  items: order.order_items?.map((item: any) => ({
                    productId: item.product_id,
                    productName: item.product_name,
                    quantity: item.quantity,
                    price: item.price,
                    pillQuantity: item.pill_quantity
                  })) || [],
                  totalAmount: order.total_amount,
                  status: order.status as 'payment_pending' | 'payment_confirmed' | 'shipping' | 'delivered' | 'cancelled' | 'refunded',
                  paymentStatus: order.payment_status as 'pending' | 'paid' | 'failed' | 'refunded',
                  shippingAddress: order.shipping_address,
                  trackingNumber: order.tracking_number || undefined,
                  createdAt: order.created_at,
                  updatedAt: order.updated_at
                }));
                setOrders(transformedOrders);
              }
            } catch (error) {
              devLog.error('❌ Failed to reload orders:', error);
            }
          }, 100);
        }
      )
      .subscribe();

    const reviewsSubscription = supabase
      .channel('reviews-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reviews' },
        async (payload: any) => {
          devLog.info('🔄 Real-time: Reviews table changed', payload);
          setTimeout(async () => {
            try {
              const reviewsData = await reviewApi.getAllReviews();
              setReviews(reviewsData);
              devLog.info('✅ Real-time: Reviews reloaded');
            } catch (error) {
              devLog.error('❌ Failed to reload reviews:', error);
            }
          }, 100);
        }
      )
      .subscribe();

    const inquiriesSubscription = supabase
      .channel('inquiries-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inquiries' },
        async (payload: any) => {
          devLog.info('🔄 Real-time: Inquiries table changed', payload);
          setTimeout(async () => {
            try {
              const inquiriesData = await inquiryApi.getAllInquiries();
              setInquiries(inquiriesData);
              devLog.info('✅ Real-time: Inquiries reloaded');
            } catch (error) {
              devLog.error('❌ Failed to reload inquiries:', error);
            }
          }, 100);
        }
      )
      .subscribe();

    const couponsSubscription = supabase
      .channel('coupons-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'coupons' },
        async (payload: any) => {
          devLog.info('🔄 Real-time: Coupons table changed', payload);
          setTimeout(async () => {
            try {
              const couponsData = await couponApi.getAll();
              setCoupons(couponsData);
              devLog.info('✅ Real-time: Coupons reloaded');
            } catch (error) {
              devLog.error('❌ Failed to reload coupons:', error);
            }
          }, 100);
        }
      )
      .subscribe();

    return () => {
      ordersSubscription.unsubscribe();
      reviewsSubscription.unsubscribe();
      inquiriesSubscription.unsubscribe();
      couponsSubscription.unsubscribe();
    };
  }, []);

  // Initialize data with Supabase
  useEffect(() => {
    const initializeData = async () => {
      try {
        devLog.info('🚀 AdminContext: Starting Supabase data initialization...');
        
        // Prevents duplicate requests: products, categories come from SupabaseDataContext
        // Load AdminContext-specific data only.
        // NOTE: `loadOrders()` goes through the `admin-api` Edge Function which
        // rejects anonymous calls with 403 — so we defer it until the user is
        // authenticated as admin (see separate useEffect on `isAdminLoggedIn`).
        await Promise.all([
          loadProductPrices(),
          loadReviews(),
          loadInquiries(),
          loadNotices(),
          loadCoupons()
        ]);
        
        // Auto-login for dev environment (localhost only)
        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true' && isLocalDev) {
          devLog.info('Dev environment: attempting Supabase Auth auto-login...');

          const devEmail = process.env.REACT_APP_DEV_ADMIN_EMAIL || 'admin@liberacure.com';
          const devPassword = process.env.REACT_APP_DEV_ADMIN_PASSWORD || 'your-password';

          // Login with Supabase Auth (to pass RLS policies)
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: devEmail,
            password: devPassword,
          });

          if (authError || !authData.user) {
            devLog.error('Dev auto-login failed:', authError?.message);
          } else {
            // Check admin permission in user_profiles
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', authData.user.id)
              .single();

            if (profile && ['ADMIN', 'SUPER_ADMIN'].includes(profile.grade)) {
              setIsAdminLoggedIn(true);
              setAdminUser({
                id: authData.user.id,
                name: profile.name || 'Admin',
                email: authData.user.email || devEmail,
                role: profile.grade === 'SUPER_ADMIN' ? 'super_admin' as const : 'admin' as const
              });
              devLog.info('Dev auto-login successful:', authData.user.email);
              // loadOrders() is invoked by the isAdminLoggedIn useEffect below
            } else {
              devLog.error('No admin permission');
            }
          }
        }
        
        devLog.info('✅ AdminContext: Supabase data initialization completed');
        
      } catch (error) {
        devLog.error('❌ AdminContext: Data initialization failed:', error);
        // Fallback data: products, categories handled by SupabaseDataContext
        // Set fallback for AdminContext-specific data only
        setReviews(getInitialReviews());
        setOrders(getInitialOrders());
        setInquiries(getInitialInquiries());
        setNotices(getInitialNotices());
      } finally {
        setAdminDataLoading(false);
      }
    };

    // setupRealtimeSubscriptions is lifted to component scope above.

    // Load products from Supabase
    // Prevents duplicate requests: loadProducts disabled - handled by SupabaseDataContext
    /* 
    const loadProducts = async () => {
      try {
        devLog.info('🔄 AdminContext: Loading products from Supabase...');
        const supabaseProducts = await productApi.getAll();
        setProducts(supabaseProducts);
        devLog.info(`✅ AdminContext: Loaded ${supabaseProducts.length} products from Supabase`);
        devLog.info('🚀 DEBUG: First 5 product IDs:', supabaseProducts.slice(0, 5).map(p => p.id));
        devLog.info('🚀 DEBUG: Looking for cenforce-50mg:', supabaseProducts.find(p => p.id === 'cenforce-50mg'));
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load products from Supabase:', error);
      }
    };
    */

    // Legacy loader: products are now fetched with nested variants+prices by SupabaseDataContext,
    // so this no-ops. The productPrices Map is rebuilt in a derived effect below whenever
    // supabaseProducts changes.
    const loadProductPrices = async () => {
      devLog.info('🔄 AdminContext: loadProductPrices is now derived from products (no-op)');
    };

    // Load reviews from Supabase
    const loadReviews = async () => {
      try {
        devLog.info('📝 AdminContext: Loading reviews from Supabase...');
        const reviewsData = await reviewApi.getAllReviews();
        setReviews(reviewsData);
        devLog.info('✅ AdminContext: Reviews loaded:', reviewsData.length);
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load reviews:', error);
      }
    };


    const loadInquiries = async () => {
      try {
        devLog.info('💬 AdminContext: Loading inquiries from Supabase...');
        const inquiriesData = await inquiryApi.getAllInquiries();
        setInquiries(inquiriesData);
        devLog.info('✅ AdminContext: Inquiries loaded:', inquiriesData.length);
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load inquiries:', error);
        setInquiries(getInitialInquiries());
      }
    };

    // Dummy function replacing old loadReviews (backward compatibility)
    const loadReviewsOld = async () => {
      try {
        const { data: supabaseReviews, error } = await supabase
          .from('reviews')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // Transform Supabase data to match Review type
        const transformedReviews = supabaseReviews?.map((review: any) => ({
          id: review.id,
          productId: review.product_id,
          productName: '', // Will be filled by product lookup
          userId: review.user_id,
          userName: 'Anonymous', // Default value
          userEmail: '', // Default value
          rating: review.rating,
          title: review.title || '',
          content: review.content,
          status: 'approved' as const, // Default status
          createdAt: review.created_at,
          updatedAt: review.updated_at
        })) || [];
        
        setReviews(transformedReviews);
        devLog.info(`✅ AdminContext: Loaded ${transformedReviews.length} reviews from Supabase`);
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load reviews from Supabase:', error);
        setReviews(getInitialReviews());
      }
    };


    // Load inquiries, health articles, and notices - similar pattern


    const loadNotices = async () => {
      try {
        // For now, use dummy data
        setNotices(getInitialNotices());
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load notices:', error);
        setNotices(getInitialNotices());
      }
    };

    const loadCoupons = async () => {
      try {
        devLog.info('🔄 AdminContext: Loading coupons from Supabase...');
        const couponsData = await couponApi.getAll();
        setCoupons(couponsData);
        devLog.info(`✅ AdminContext: Loaded ${couponsData.length} coupons`);
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load coupons:', error);
        setCoupons([]);
      }
    };

    // Load categories - Use Supabase as primary source
    // Prevents duplicate requests: loadCategories disabled - handled by SupabaseDataContext
    const loadCategories = async () => { return; // Disabled
      try {
        devLog.info('🔄 AdminContext: Loading categories from Supabase...');
        devLog.info('🐛 DEBUG: categoryApi.getAll function:', typeof categoryApi.getAll);
        
        // Try to load from Supabase first
        const supabaseCategories = await categoryApi.getAll();
        devLog.info('🐛 DEBUG: Raw supabaseCategories result:', supabaseCategories);
        if (supabaseCategories.length > 0) {
          devLog.info('✅ Categories loaded via SupabaseDataContext, AdminContext disabled');
          devLog.info('✅ AdminContext: Categories loaded from Supabase:', supabaseCategories.length);
          devLog.info('🔍 First 5 category IDs:', supabaseCategories.slice(0, 5).map(c => ({ id: c.id, name: c.name })));
          return;
        }
        
        // Fallback to categories.json if Supabase is empty
        devLog.info('⚠️ AdminContext: No categories in Supabase, loading from categories.json...');
        const response = await fetch('/categories.json');
        const data = await response.json();
        devLog.info('✅ Categories loaded via SupabaseDataContext, AdminContext disabled');
        
        // Migrate to Supabase for future use
        devLog.info('🔄 AdminContext: Migrating categories to Supabase...');
        for (const category of data) {
          await categoryApi.create(category.name, category.parentId);
        }
        devLog.info('✅ AdminContext: Categories migrated to Supabase');
      } catch (error) {
        devLog.error('❌ AdminContext: Failed to load categories:', error);
        // Fallback default hierarchical categories based on product_category.txt
        const fallbackCategories: Category[] = [
          // Sexual health categories
          { id: 1, name: 'ED Medications', level: 0 },
          { id: 2, name: 'Tadalafil', parentId: 1, level: 1 },
          { id: 3, name: 'Sildenafil', parentId: 1, level: 1 },
          { id: 4, name: 'Vardenafil', parentId: 1, level: 1 },
          { id: 5, name: 'Udenafil', parentId: 1, level: 1 },
          { id: 6, name: 'Avanafil', parentId: 1, level: 1 },
          { id: 7, name: 'Dapoxetine', parentId: 1, level: 1 },
          { id: 8, name: 'Sildenafil + Dapoxetine', parentId: 1, level: 1 },
          { id: 9, name: 'Tadalafil + Dapoxetine', parentId: 1, level: 1 },
          { id: 10, name: 'Vardenafil + Dapoxetine', parentId: 1, level: 1 },
          { id: 11, name: 'Avanafil + Dapoxetine', parentId: 1, level: 1 },
          { id: 12, name: 'Bundle Sets', parentId: 1, level: 1 },
          { id: 13, name: 'Other Products', parentId: 1, level: 1 },

          // Hair loss categories
          { id: 14, name: 'Hair Loss', level: 0 },
          { id: 15, name: 'Finasteride', parentId: 14, level: 1 },
          { id: 16, name: 'Dutasteride', parentId: 14, level: 1 },
          { id: 17, name: 'Minoxidil', parentId: 14, level: 1 },
          { id: 18, name: 'Spironolactone', parentId: 14, level: 1 },
          { id: 19, name: 'Combination', parentId: 14, level: 1 },
          { id: 20, name: 'Finasteride + Minoxidil', parentId: 19, level: 2 },
          { id: 21, name: 'Dutasteride + Minoxidil', parentId: 19, level: 2 },
          { id: 22, name: 'Finasteride + Spironolactone', parentId: 19, level: 2 },
          { id: 23, name: 'Topical Finasteride', parentId: 14, level: 1 },
          { id: 24, name: 'For Women', parentId: 14, level: 1 },
          { id: 25, name: 'Other', parentId: 14, level: 1 },

          // Acne/skin categories
          { id: 26, name: 'Acne & Skincare', level: 0 },
          { id: 27, name: 'Isotretinoin (Accutane)', parentId: 26, level: 1 },
          { id: 28, name: 'Doxycycline', parentId: 26, level: 1 },
          { id: 29, name: 'Minocycline', parentId: 26, level: 1 },
          { id: 30, name: 'Retinol', parentId: 26, level: 1 },
          { id: 31, name: 'Adapalene', parentId: 26, level: 1 },
          { id: 32, name: 'Tretinoin', parentId: 26, level: 1 },
          { id: 33, name: 'Benzoyl Peroxide', parentId: 26, level: 1 },
          { id: 34, name: 'Nadifloxacin', parentId: 26, level: 1 },
          { id: 35, name: 'Azelaic Acid', parentId: 26, level: 1 },
          { id: 36, name: 'Other', parentId: 26, level: 1 },

          // Diet/health categories
          { id: 37, name: 'Weight Management', level: 0 },
          { id: 38, name: 'Semaglutide (Rybelsus)', parentId: 37, level: 1 },
          { id: 39, name: 'Fat Absorption Blockers', parentId: 37, level: 1 },
          { id: 40, name: 'Carb Absorption Blockers', parentId: 37, level: 1 },
          { id: 41, name: 'Contrave', parentId: 37, level: 1 },
          { id: 42, name: 'Teneligliptin', parentId: 37, level: 1 },
          { id: 43, name: 'Topiramate', parentId: 37, level: 1 },
          { id: 44, name: 'Diuretics', parentId: 37, level: 1 },
          { id: 45, name: 'Supplements', parentId: 37, level: 1 },
          { id: 46, name: 'Other', parentId: 37, level: 1 },

          // Beauty/other categories
          { id: 47, name: 'Beauty & Other', level: 0 },
          { id: 48, name: 'Skin Brightening', parentId: 47, level: 1 },
          { id: 49, name: 'Eyelash Growth', parentId: 47, level: 1 },
          { id: 50, name: 'Placentrex', parentId: 47, level: 1 },
          { id: 51, name: 'Ivermectin Cream', parentId: 47, level: 1 },
          { id: 52, name: 'Hydroquinone', parentId: 47, level: 1 },
          { id: 53, name: 'Acyclovir', parentId: 47, level: 1 },
          { id: 54, name: 'Trifarotene (4th Gen Retinoid)', parentId: 47, level: 1 }
        ];
        devLog.info('✅ Categories loaded via SupabaseDataContext, AdminContext disabled');
        devLog.info('⚠️ AdminContext: Using fallback categories');
      }
    };
    

    // Auth state managed by useAuth() - duplicate subscriptions removed

    // Initial data load and admin permission check
    initializeData();
    checkAdminStatus();

    // Cleanup handled by the cleanup function returned from setupRealtimeSubscriptions
    // No longer needs cleanup after removing auth subscription
  }, []);

  // Load admin-only data (orders via admin-api) + open realtime channels
  // whenever admin auth transitions to true. Covers dev auto-login, explicit
  // `loginAdmin()`, and page refresh via `checkAdminStatus()`.
  useEffect(() => {
    if (!isAdminLoggedIn) return;
    loadOrders();
    return setupRealtimeSubscriptions();
    // loadOrders is a stable closure over setOrders — safe to omit from deps.
    // setupRealtimeSubscriptions is wrapped in useCallback with [] deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLoggedIn]);

  // Re-check admin permission when useAuth state changes
  useEffect(() => {
    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading]);

  // Product management functions - Integrated with Supabase
  const addProduct = async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      devLog.info('🔄 AdminContext: Adding product to Supabase...', product.name);
      const newProduct = await productApi.create(product);
      if (newProduct) {
        devLog.info('✅ AdminContext: Product added successfully (will be reflected via SupabaseDataContext)');
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add product:', error);
    }
  };

  const updateProduct = async (productId: string, updates: Partial<Product>) => {
    try {
      devLog.info('🚀 DEBUG: AdminContext.updateProduct called');
      devLog.info('🚀 DEBUG: Product ID:', productId);
      devLog.info('🚀 DEBUG: Updates:', updates);
      devLog.info('🚀 DEBUG: Current products count:', products.length);
      
      const updatedProduct = await productApi.update(productId, updates);
      devLog.info('🚀 DEBUG: productApi.update result:', updatedProduct);
      
      if (updatedProduct) {
        devLog.info('✅ AdminContext: Product updated successfully (will be reflected via SupabaseDataContext)');
        devLog.info('🚀 DEBUG: Updated product:', updatedProduct);
      } else {
        devLog.error('❌ AdminContext: productApi.update returned null - update failed');
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to update product:', error);
      if (error instanceof Error) {
        devLog.error('❌ AdminContext: Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      } else {
        devLog.error('❌ AdminContext: Unknown error type:', typeof error, error);
      }
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      devLog.info('🔄 AdminContext: Deleting product from Supabase...', productId);
      const success = await productApi.delete(productId);
      if (success) {
        devLog.info('✅ AdminContext: Product deleted successfully (will be reflected via SupabaseDataContext)');
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to delete product:', error);
    }
  };

  const getProduct = async (productId: string) => {
    // Try local first for performance
    const localProduct = products.find(product => product.id === productId);
    if (localProduct) {
      return localProduct;
    }
    
    // If not found locally, fetch from Supabase
    try {
      devLog.info('🔄 AdminContext: Fetching product from Supabase...', productId);
      const supabaseProduct = await productApi.getById(productId);
      if (supabaseProduct) {
        // Product will be automatically added via SupabaseDataContext subscription
        return supabaseProduct;
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to fetch product from Supabase:', error);
    }
    
    return undefined;
  };

  const getProductSync = (productId: string) => {
    return products.find(product => product.id === productId);
  };


  // Review management functions
  const addReview = async (reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      devLog.info('📝 AdminContext: Adding review to Supabase...', reviewData);
      
      // UUID generation function
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      // Prepare data for DB (using snake_case fields)
      const dbReviewData = {
        product_id: reviewData.productId || reviewData.product_id || '',
        user_id: reviewData.userId || reviewData.user_id || generateUUID(),
        user_name: reviewData.userName,
        user_email: reviewData.userEmail,
        rating: reviewData.rating,
        title: reviewData.title || '',
        content: reviewData.content,
        images: reviewData.images || null, // Image URL array
        status: reviewData.status || 'pending',
        is_verified: (reviewData.status === 'approved') || false
      };
      
      const newReview = await reviewApi.addReview(dbReviewData);
      
      if (newReview) {
        devLog.info('✅ AdminContext: Review added successfully');
        // Real-time subscription will handle the state update
        return newReview;
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add review:', error);
      // Fallback to local state for backwards compatibility
      const localReview: Review = {
        ...reviewData,
        id: `review_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setReviews(prev => [localReview, ...prev]);
      return localReview;
    }
  };

  const updateReviewStatus = async (reviewId: string, status: 'pending' | 'approved' | 'rejected') => {
    try {
      devLog.info('📝 AdminContext: Updating review status in Supabase...', { reviewId, status });
      await reviewApi.updateReviewStatus(reviewId, status);
      devLog.info('✅ AdminContext: Review status updated successfully');
      // Real-time subscription will handle the state update
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to update review status:', error);
      // Fallback to local state
      setReviews(prev =>
        prev.map(review =>
          review.id === reviewId
            ? { ...review, status, updatedAt: new Date().toISOString() }
            : review
        )
      );
    }
  };

  const deleteReview = (reviewId: string) => {
    setReviews(prev => prev.filter(review => review.id !== reviewId));
  };

  const updateReview = (reviewId: string, updates: Partial<Review>) => {
    setReviews(prev =>
      prev.map(review =>
        review.id === reviewId
          ? { ...review, ...updates, updatedAt: new Date().toISOString() }
          : review
      )
    );
  };

  const approveReview = async (reviewId: string, author?: string) => {
    try {
      devLog.info('✅ AdminContext: Approving review...', { reviewId, author });

      // 1. Get review info
      const review = reviews.find(r => r.id === reviewId);
      if (!review) {
        devLog.error('❌ Review not found:', reviewId);
        return;
      }

      // 2. Update Supabase
      await reviewApi.updateReviewStatus(reviewId, 'approved');
      devLog.info('✅ AdminContext: Review approved successfully');

      // 3. Auto-grant reward points (1P = $1 direct mapping)
      //    Photo review: 10P = $10 credit
      //    Text review:   5P = $5 credit
      //    Best review (monthly): $25 credit — awarded manually by admin
      if (review.userId) {
        const isPhotoReview = review.images && review.images.length > 0;
        const pointAmount = isPhotoReview ? 10 : 5;
        const reviewType = isPhotoReview ? 'Photo Review' : 'Text Review';

        devLog.info(`💰 Granting ${pointAmount}P for ${reviewType}`, {
          reviewId,
          userId: review.userId,
          hasImages: isPhotoReview,
          imageCount: review.images?.length || 0
        });

        const success = await pointApi.addPoints(
          review.userId,
          pointAmount,
          'review_reward',
          `${reviewType} reward points`
        );

        if (success) {
          devLog.info(`✅ ${pointAmount}P granted successfully to user ${review.userId}`);
        } else {
          devLog.error('❌ Failed to grant points');
        }
      } else {
        devLog.warn('⚠️ Review has no userId, skipping point grant');
      }

      // 4. Update local state immediately (real-time subscription + instant reflection)
      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId
            ? { ...r, status: 'approved' as const }
            : r
        )
      );
      devLog.info('✅ AdminContext: Local state updated');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to approve review:', error);
      // Do not update local state if DB update fails (maintain data consistency)
      alert('An error occurred while approving the review. Please try again.');
    }
  };

  const rejectReview = async (reviewId: string, author?: string) => {
    try {
      devLog.info('❌ AdminContext: Rejecting review...', { reviewId, author });

      // Update Supabase
      await reviewApi.updateReviewStatus(reviewId, 'rejected');
      devLog.info('✅ AdminContext: Review rejected successfully');

      // Update local state immediately
      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId
            ? { ...r, status: 'rejected' as const }
            : r
        )
      );
      devLog.info('✅ AdminContext: Local state updated');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to reject review:', error);
      alert('An error occurred while rejecting the review. Please try again.');
    }
  };

  const replyToReview = async (reviewId: string, reply: string, author?: string) => {
    try {
      devLog.info('💬 AdminContext: Adding admin reply...', { reviewId, author });

      // Update Supabase
      await reviewApi.addAdminReply(reviewId, reply);
      devLog.info('✅ AdminContext: Admin reply added successfully');

      // Update local state immediately
      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId
            ? { ...r, adminReply: reply }
            : r
        )
      );
      devLog.info('✅ AdminContext: Local state updated');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add admin reply:', error);
      alert('An error occurred while adding admin reply. Please try again.');
    }
  };

  // Order management functions
  const updateOrderStatus = async (orderId: string, status: 'payment_pending' | 'payment_confirmed' | 'shipping' | 'delivered' | 'cancelled' | 'refunded' | 'trash') => {
    const success = await orderApi.updateStatus(orderId, status);
    if (success) {
      await loadOrders(); // Refresh list on success
    } else {
      alert('Failed to update order status.');
    }
  };

  const updatePaymentStatus = async (orderId: string, status: 'pending' | 'paid' | 'failed' | 'refunded') => {
    try {
      devLog.info(`Payment status update started: ${orderId} -> ${status}`);

      // Call Supabase payment status update API
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          payment_status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) {
        devLog.error('Payment status DB update failed:', error);
        alert('Failed to update payment status. Please try again.');
        return;
      }

      devLog.info('Payment status DB update success');
      // Update local state
      setOrders(prev =>
        prev.map(order =>
          order.id === orderId
            ? { ...order, paymentStatus: status, updatedAt: new Date().toISOString() }
            : order
        )
      );
    } catch (error) {
      devLog.error('Payment status update error:', error);
      alert('An error occurred while updating payment status.');
    }
  };

  const updateOrder = (orderId: string, updates: Partial<Order>) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? { ...order, ...updates, updatedAt: new Date().toISOString() }
          : order
      )
    );
  };

  const addTrackingNumber = async (orderId: string, trackingNumber: string, shippingCompany?: string) => {
    try {
        devLog.info(`🔄 Adding tracking number via Edge Function for order: ${orderId}`);
        const success = await orderApi.addTrackingNumber(orderId, trackingNumber, shippingCompany || '');
        if (success) {
            devLog.info('✅ Tracking number added successfully.');
            await loadOrders(); // Refresh list on success
        } else {
            devLog.error('Failed to add tracking number via API.');
            alert('Failed to save tracking number.');
        }
    } catch (error) {
        devLog.error('Error in addTrackingNumber context function:', error);
        alert('An error occurred while saving tracking number.');
    }
  };

  // Order deletion (DB + local state simultaneous handling)
  const deleteOrder = async (orderId: string): Promise<boolean> => {
    try {
      devLog.info('AdminContext: Starting order deletion:', orderId);

      // 1. Delete order from Supabase DB (using orderApi.delete)
      const success = await orderApi.delete(orderId);

      if (success) {
        devLog.info('AdminContext: DB deletion successful');
        // 2. Remove order from local state
        setOrders(prev => prev.filter(order => order.id !== orderId));
        devLog.info('AdminContext: Order removed from local state');
        return true;
      } else {
        devLog.error('AdminContext: DB deletion failed');
        return false;
      }
    } catch (error) {
      devLog.error('AdminContext: Error during order deletion:', error);
      return false;
    }
  };

  // Trash-related functions

  // Load trash orders
  const loadTrashOrders = async (): Promise<void> => {
    try {
      devLog.info('AdminContext: Loading trash orders');
      const trashOrdersData = await orderApi.getTrashOrders();
      setTrashOrders(trashOrdersData);
      devLog.info(`AdminContext: Loaded ${trashOrdersData.length} trash orders`);
    } catch (error) {
      devLog.error('AdminContext: Failed to load trash orders:', error);
      setTrashOrders([]);
    }
  };

  // Move order to trash
  const moveOrderToTrash = async (orderId: string): Promise<boolean> => {
    try {
      devLog.info('AdminContext: Moving order to trash:', orderId);

      // 1. Change status to 'trash' in DB
      const success = await orderApi.moveToTrash(orderId);

      if (success) {
        // 2. Remove order from local orders state
        const movedOrder = orders.find(order => order.id === orderId);
        if (movedOrder) {
          setOrders(prev => prev.filter(order => order.id !== orderId));
          // 3. Add to trash state (change status to 'trash')
          setTrashOrders(prev => [{ ...movedOrder, status: 'trash' as any }, ...prev]);
        }
        devLog.info('AdminContext: Order moved to trash successfully');
        return true;
      } else {
        devLog.error('AdminContext: Failed to move order to trash');
        return false;
      }
    } catch (error) {
      devLog.error('AdminContext: Error moving to trash:', error);
      return false;
    }
  };

  // Restore order from trash
  const restoreOrderFromTrash = async (orderId: string, previousStatus: string = 'payment_pending'): Promise<boolean> => {
    try {
      devLog.info('AdminContext: Restoring order from trash:', orderId, '->', previousStatus);

      // 1. Change status to previous state in DB
      const success = await orderApi.restoreFromTrash(orderId, previousStatus);

      if (success) {
        // 2. Remove order from trash local state
        const restoredOrder = trashOrders.find(order => order.id === orderId);
        if (restoredOrder) {
          setTrashOrders(prev => prev.filter(order => order.id !== orderId));
          // 3. Add to regular order list (change status to restored state)
          setOrders(prev => [{ ...restoredOrder, status: previousStatus as any }, ...prev]);
        }
        devLog.info('AdminContext: Order restored successfully');
        return true;
      } else {
        devLog.error('AdminContext: Failed to restore order');
        return false;
      }
    } catch (error) {
      devLog.error('AdminContext: Error restoring order:', error);
      return false;
    }
  };

  // Inquiry management functions
  const updateInquiryStatus = (inquiryId: string, status: 'pending' | 'answered' | 'closed') => {
    setInquiries(prev =>
      prev.map(inquiry =>
        inquiry.id === inquiryId
          ? { ...inquiry, status, updatedAt: new Date().toISOString() }
          : inquiry
      )
    );
  };

  const addInquiryResponse = async (inquiryId: string, response: string) => {
    try {
      devLog.info('💬 AdminContext: Adding inquiry response to Supabase...', { inquiryId, response });
      await inquiryApi.addAdminResponse(inquiryId, response, adminUser?.name);
      devLog.info('✅ AdminContext: Inquiry response added successfully');
      // Real-time subscription will handle the state update
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add inquiry response:', error);
      // Fallback to local state
      setInquiries(prev =>
        prev.map(inquiry =>
          inquiry.id === inquiryId
            ? { 
                ...inquiry, 
                adminReply: response,
                status: 'answered' as const,
                updatedAt: new Date().toISOString()
              }
            : inquiry
        )
      );
    }
  };

  const replyToInquiry = async (inquiryId: string, response: string, author?: string) => {
    try {
      await addInquiryResponse(inquiryId, response);
    } catch (error) {
      // Fallback to local state
      setInquiries(prev =>
        prev.map(inquiry =>
          inquiry.id === inquiryId
            ? { 
                ...inquiry, 
                adminReply: response,
                answeredBy: author || 'Admin',
                answeredAt: new Date().toISOString(),
                status: 'answered' as const,
                updatedAt: new Date().toISOString()
              }
            : inquiry
        )
      );
    }
  };

  const addInquiry = async (inquiry: Omit<Inquiry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      devLog.info('💬 AdminContext: Adding inquiry to Supabase...', inquiry);
      const newInquiry = await inquiryApi.addInquiry({
        userId: inquiry.userId,
        userName: inquiry.userName,
        userEmail: inquiry.userEmail,
        category: inquiry.category,
        title: inquiry.title,
        content: inquiry.content,
        orderNumber: inquiry.orderNumber
      });
      
      if (newInquiry) {
        devLog.info('✅ AdminContext: Inquiry added successfully');
        // Real-time subscription will handle the state update
        return newInquiry;
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add inquiry:', error);
      // Fallback to local state for backwards compatibility
      const localInquiry: Inquiry = {
        ...inquiry,
        id: `inquiry_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setInquiries(prev => [...prev, localInquiry]);
      return localInquiry;
    }
  };

  const updateInquiry = (inquiryId: string, updates: Partial<Inquiry>) => {
    setInquiries(prev =>
      prev.map(inquiry =>
        inquiry.id === inquiryId
          ? { ...inquiry, ...updates, updatedAt: new Date().toISOString() }
          : inquiry
      )
    );
  };


  // Admin authentication functions - Supabase Auth based
  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    try {
      devLog.info('Admin authentication attempt:', email);

      // 1. Attempt login via Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        devLog.error('Admin login failed:', error?.message || error);
        return false;
      }

      if (!data.user) {
        devLog.error('No user information found');
        return false;
      }

      // 2. Verify admin permissions from user_profiles
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        devLog.error('Profile lookup failed:', profileError.message);
        return false;
      }

      // 3. Validate admin permissions (only ADMIN or SUPER_ADMIN allowed)
      if (!profile || !['ADMIN', 'SUPER_ADMIN'].includes(profile.grade)) {
        devLog.error('No admin permissions:', profile?.grade);
        // Sign out if not admin
        await supabase.auth.signOut();
        return false;
      }

      // 4. Set admin info
      const admin = {
        id: data.user.id,
        name: profile.name || 'Admin',
        email: data.user.email || email,
        role: profile.grade === 'SUPER_ADMIN' ? 'super_admin' as const : 'admin' as const
      };

      setAdminUser(admin);
      setIsAdminLoggedIn(true);
      // loadOrders() is invoked by the isAdminLoggedIn useEffect

      devLog.info('Admin authentication successful:', admin.name, `(${admin.role})`);
      return true;

    } catch (error) {
      devLog.error('Admin authentication error:', error);
      return false;
    }
  };

  // Category management functions
  const addCategory = async (name: string, parentId?: number) => {
    try {
      devLog.info('🔄 AdminContext: Adding category to Supabase:', name);
      const newCategory = await categoryApi.create(name, parentId);
      
      if (newCategory) {
        devLog.info('✅ AdminContext: Category added successfully (will be reflected via SupabaseDataContext):', newCategory.name);
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to add category:', error);
    }
  };

  const updateCategory = async (id: number, updates: Partial<Category>) => {
    try {
      devLog.info('🔄 AdminContext: Updating category in Supabase:', id);
      const updatedCategory = await categoryApi.update(id, updates);
      
      if (updatedCategory) {
        devLog.info('✅ AdminContext: Category updated successfully (will be reflected via SupabaseDataContext):', updatedCategory.name);
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to update category:', error);
    }
  };

  const deleteCategory = async (id: number) => {
    try {
      devLog.info('🔄 AdminContext: Deleting category from Supabase:', id);
      const success = await categoryApi.delete(id);
      
      if (success) {
        devLog.info('✅ AdminContext: Category deleted successfully (will be reflected via SupabaseDataContext)');
      }
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to delete category:', error);
    }
  };

  const getCategoryTree = (): Category[] => {
    const buildTree = (parentId?: number): Category[] => {
      return categories
        .filter(cat => cat.parentId === parentId)
        .map(cat => ({
          ...cat,
          children: buildTree(cat.id)
        }));
    };
    return buildTree();
  };

  const getCategoryPath = (categoryId: number): Category[] => {
    const path: Category[] = [];
    let currentId: number | undefined = categoryId;
    
    while (currentId !== undefined) {
      const category = categories.find(c => c.id === currentId);
      if (category) {
        path.unshift(category);
        currentId = category.parentId;
      } else {
        currentId = undefined;
      }
    }
    return path;
  };

  const flatCategories = categories;

  const logoutAdmin = async () => {
    try {
      devLog.info('Admin logout attempt');

      // Sign out from Supabase Auth
      const { error } = await supabase.auth.signOut();

      if (error) {
        devLog.error('Logout error:', error?.message || error);
      }

      // Reset admin state + clear admin-only cached data
      setAdminUser(null);
      setIsAdminLoggedIn(false);
      setOrders([]);

      devLog.info('Admin logout complete');
    } catch (error) {
      devLog.error('Logout processing failed:', error);
      // Reset local state even if error occurs
      setAdminUser(null);
      setIsAdminLoggedIn(false);
    }
  };

  // Notice management functions
  const addNotice = (notice: Notice) => {
    setNotices(prev => [...prev, notice]);
  };

  const updateNotice = (noticeId: string, updates: Partial<Notice>) => {
    setNotices(prev =>
      prev.map(notice =>
        notice.id === noticeId
          ? { ...notice, ...updates, updatedAt: new Date().toISOString() }
          : notice
      )
    );
  };

  const deleteNotice = (noticeId: string) => {
    setNotices(prev => prev.filter(notice => notice.id !== noticeId));
  };

  // Coupon Management Functions
  const createCoupon = async (couponData: Omit<Coupon, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      devLog.info('🔄 AdminContext: Creating coupon...', couponData);
      const newCoupon = await couponApi.create(couponData);
      setCoupons(prev => [...prev, newCoupon]);
      devLog.info('✅ AdminContext: Coupon created successfully');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to create coupon:', error);
      throw error;
    }
  };

  const updateCoupon = async (couponId: string, updates: Partial<Coupon>) => {
    try {
      devLog.info('🔄 AdminContext: Updating coupon...', couponId, updates);
      await couponApi.update(couponId, updates);
      setCoupons(prev => prev.map(c => c.id === couponId ? { ...c, ...updates } : c));
      devLog.info('✅ AdminContext: Coupon updated successfully');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to update coupon:', error);
      throw error;
    }
  };

  const deleteCoupon = async (couponId: string) => {
    try {
      devLog.info('🔄 AdminContext: Deleting coupon...', couponId);
      await couponApi.delete(couponId);
      setCoupons(prev => prev.filter(c => c.id !== couponId));
      devLog.info('✅ AdminContext: Coupon deleted successfully');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to delete coupon:', error);
      throw error;
    }
  };

  const toggleCouponStatus = async (couponId: string, currentStatus: boolean) => {
    try {
      devLog.info('🔄 AdminContext: Toggling coupon status...', couponId);
      await couponApi.update(couponId, { is_active: !currentStatus });
      setCoupons(prev => prev.map(c => c.id === couponId ? { ...c, is_active: !currentStatus } : c));
      devLog.info('✅ AdminContext: Coupon status toggled successfully');
    } catch (error) {
      devLog.error('❌ AdminContext: Failed to toggle coupon status:', error);
      throw error;
    }
  };

  // Stats calculation
  const getStats = () => {
    const totalProducts = products.length;
    const totalOrders = orders.length;
    const totalRevenue = orders
      .filter(order => order.paymentStatus === 'paid')
      .reduce((sum, order) => sum + order.totalAmount, 0);
    const pendingInquiries = inquiries.filter(inquiry => inquiry.status === 'pending').length;
    const pendingReviews = reviews.filter(review => review.status === 'pending').length;

    return {
      totalProducts,
      totalOrders,
      totalRevenue,
      pendingInquiries,
      pendingReviews
    };
  };

  // ProductPrice related functions
  const getProductPrices = (productId: string): ProductPrice[] => {
    return productPrices.get(productId) || [];
  };

  // Volume discount calculation function
  const calculateVolumeDiscount = (productId: string, quantity: number): number => {
    const prices = getProductPrices(productId);
    
    if (!prices || prices.length === 0) {
      return 0;
    }

    // 1. Set the minimum quantity as the base unit
    const minQuantity = Math.min(...prices.map(p => p.quantity));
    const basePrice = prices.find(p => p.quantity === minQuantity);
    
    if (!basePrice) {
      return 0;
    }

    // 2. Find the actual price for the given quantity
    const targetPrice = prices.find(p => p.quantity === quantity);
    
    if (!targetPrice) {
      return 0;
    }

    // 3. Calculate proportional price relative to base quantity (expected price)
    const expectedPrice = basePrice.sale_price * (quantity / basePrice.quantity);
    
    // 4. Discount rate = (expected price - actual price) / expected price * 100
    const discountRate = ((expectedPrice - targetPrice.sale_price) / expectedPrice) * 100;
    
    // Return 0% for negative values, rounded to integer
    return Math.max(0, Math.round(discountRate));
  };

  // Resolve default variant id for a given product (family head)
  const resolveDefaultVariantId = (productId: string): string | null => {
    const product = products.find(p => p.id === productId);
    if (!product) return null;
    const variants = (product as any).variants || [];
    if (variants.length === 0) return null;
    const def = variants.find((v: any) => v.is_default) || variants[0];
    return def?.id || null;
  };

  const upsertProductPrice = async (productId: string, priceData: Omit<ProductPrice, 'id' | 'variant_id' | 'created_at' | 'updated_at'>): Promise<void> => {
    try {
      const variantId = resolveDefaultVariantId(productId);
      if (!variantId) throw new Error(`No variant found for product ${productId}`);

      const { data, error } = await supabase
        .from('product_prices')
        .upsert({
          variant_id: variantId,
          quantity: priceData.quantity,
          original_price: priceData.original_price,
          sale_price: priceData.sale_price,
          discount_rate: priceData.discount_rate
        }, {
          onConflict: 'variant_id,quantity'
        })
        .select()
        .single();

      if (error) throw error;

      // Update local Map (keyed by productId)
      setProductPrices(prev => {
        const updated = new Map(prev);
        const prices = [...(updated.get(productId) || [])];
        const existingIndex = prices.findIndex(p => p.quantity === priceData.quantity);
        if (existingIndex >= 0) prices[existingIndex] = data as ProductPrice;
        else prices.push(data as ProductPrice);
        updated.set(productId, prices);
        return updated;
      });

      devLog.info('✅ Product price upserted successfully:', data);
    } catch (error) {
      devLog.error('Failed to update product price:', error);
      throw error;
    }
  };

  const addProductPrice = async (productId: string, priceData: Omit<ProductPrice, 'id' | 'variant_id' | 'created_at' | 'updated_at'>): Promise<void> => {
    try {
      const variantId = resolveDefaultVariantId(productId);
      if (!variantId) throw new Error(`No variant found for product ${productId}`);

      const { data, error } = await supabase
        .from('product_prices')
        .insert([{
          variant_id: variantId,
          quantity: priceData.quantity,
          original_price: priceData.original_price,
          sale_price: priceData.sale_price,
          discount_rate: priceData.discount_rate
        }])
        .select()
        .single();

      if (error) throw error;

      const newPrice = data as ProductPrice;
      setProductPrices(prev => {
        const newMap = new Map(prev);
        const existingPrices = newMap.get(productId) || [];
        newMap.set(productId, [...existingPrices, newPrice]);
        return newMap;
      });
    } catch (error) {
      devLog.error('Failed to add product price:', error);
      throw error;
    }
  };

  const deleteProductPrice = async (priceId: number): Promise<void> => {
    try {
      // Find which product's Map entry this belongs to
      let owningProductId: string | null = null;
      productPrices.forEach((prices: ProductPrice[], pid: string) => {
        if (owningProductId) return;
        if (prices.some((p: ProductPrice) => p.id === priceId)) owningProductId = pid;
      });

      const { error } = await supabase
        .from('product_prices')
        .delete()
        .eq('id', priceId);

      if (error) throw error;

      if (owningProductId) {
        setProductPrices(prev => {
          const newMap = new Map(prev);
          const existingPrices = newMap.get(owningProductId!) || [];
          newMap.set(owningProductId!, existingPrices.filter(price => price.id !== priceId));
          return newMap;
        });
      }
    } catch (error) {
      devLog.error('Failed to delete product price:', error);
      throw error;
    }
  };

  const contextValue: AdminContextType = {
    isLoading,
    products,
    addProduct,
    updateProduct,
    deleteProduct,
    getProduct,
    getProductSync,
    productPrices,
    getProductPrices,
    calculateVolumeDiscount,
    addProductPrice,
    upsertProductPrice,
    deleteProductPrice,
    reviews,
    addReview,
    updateReview,
    updateReviewStatus,
    approveReview,
    rejectReview,
    replyToReview,
    deleteReview,
    orders,
    updateOrder,
    updateOrderStatus,
    updatePaymentStatus,
    addTrackingNumber,
    deleteOrder, // Permanent order deletion
    
    // WordPress-style trash features
    trashOrders,
    moveOrderToTrash,
    restoreOrderFromTrash,
    loadTrashOrders,
    inquiries,
    addInquiry,
    updateInquiryStatus,
    addInquiryResponse,
    replyToInquiry,
    updateInquiry,
    notices,
    addNotice,
    updateNotice,
    deleteNotice,
    categories,
    flatCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryTree,
    getCategoryPath,
    coupons,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    toggleCouponStatus,
    isAdminLoggedIn,
    adminUser,
    loginAdmin,
    logoutAdmin,
    getStats
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

function getInitialProducts(): Product[] {
  // Return empty array to avoid type conflicts - use Supabase products only
  return [];
}


function getInitialReviews(): Review[] {
  return [
    {
      id: '1',
      productId: 'tadalista-20mg',
      productName: 'Tadalista 20mg',
      userId: 'user1',
      userName: 'J***',
      userEmail: 'user1@example.com',
      rating: 5,
      title: 'Really effective product',
      content: 'I could feel the effects in about 30 minutes, and the duration was very satisfying.',
      status: 'approved',
      createdAt: '2024-01-10T00:00:00.000Z',
      updatedAt: '2024-01-10T00:00:00.000Z'
    }
  ];
}

function getInitialOrders(): Order[] {
  return [
    {
      id: 'order_1',
      userId: 'user1',
      userName: 'John Doe',
      userEmail: 'user1@example.com',
      orderNumber: 'ORD-2024-001',
      items: [
        {
          productId: 'tadalista-20mg',
          productName: 'Tadalista 20mg',
          quantity: 1,
          price: 89
        }
      ],
      totalAmount: 89,
      status: 'delivered',
      paymentStatus: 'paid',
      shippingAddress: '123 Main St, New York, NY',
      createdAt: '2024-01-05T00:00:00.000Z',
      updatedAt: '2024-01-10T00:00:00.000Z'
    }
  ];
}

function getInitialInquiries(): Inquiry[] {
  return [
    {
      id: '1',
      userId: 'user1',
      userEmail: 'user1@example.com',
      userName: 'J***',
      category: 'product',
      title: 'Product Inquiry',
      content: 'I have a question about how to take Tadalista.',
      status: 'answered',
      adminReply: 'You can take it 30 minutes to 1 hour before activity, regardless of meals.',
      answeredBy: 'Admin',
      answeredAt: '2024-01-16T00:00:00.000Z',
      createdAt: '2024-01-15T00:00:00.000Z',
      updatedAt: '2024-01-16T00:00:00.000Z'
    }
  ];
}


function getInitialNotices(): Notice[] {
  return [
    {
      id: '1',
      title: 'Is it safe to purchase medications through an international buying service?',
      content: `Yes, it is a safe purchasing method. Purchasing medications for personal use from overseas suppliers is a well-established practice in many countries.

For personal use, you may import medications within the permitted quantity limits (typically up to a 90-day supply) without requiring additional inspection or approval.

Please always check your local regulations regarding personal medication imports.`,
      author: 'LiberaCure',
      isImportant: true,
      views: 287,
      createdAt: '2024-08-15T00:00:00.000Z',
      updatedAt: '2024-08-15T00:00:00.000Z'
    },
    {
      id: '2',
      title: 'Are the pharmaceutical manufacturers trustworthy?',
      content: `All products carried by LiberaCure are manufactured by world-class Indian pharmaceutical companies.

You can purchase with confidence.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 163,
      createdAt: '2024-08-18T00:00:00.000Z',
      updatedAt: '2024-08-18T00:00:00.000Z'
    },
    {
      id: '3',
      title: 'Same active ingredient but many brand names. Is there a difference?',
      content: `Even with the same active ingredient, there are differences between pharmaceutical companies and manufacturers, resulting in different brand names, product names, and pricing.

However, if the products contain the same ingredient at the same dosage, the effects are generally identical.

It's the same principle as cold medicines at the pharmacy -- most contain acetaminophen, but there are many different brands.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 95,
      createdAt: '2024-08-21T00:00:00.000Z',
      updatedAt: '2024-08-21T00:00:00.000Z'
    },
    {
      id: '4',
      title: 'Products come in various dosages. Which one should I buy?',
      content: `Even products with the same brand name and ingredient may come in various dosages. The appropriate dosage can vary greatly depending on each person's condition.

Therefore, a higher dosage does not necessarily mean better results.

Even if you have prior experience, we recommend determining the dosage based on professional medical advice.

If this is your first time, we recommend starting with the lowest available dosage.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 241,
      createdAt: '2024-08-24T00:00:00.000Z',
      updatedAt: '2024-08-24T00:00:00.000Z'
    },
    {
      id: '5',
      title: 'My delivery seems to be delayed.',
      content: `Typically, ordered products are shipped from the origin within 1-3 business days.

However, there may occasionally be delays due to the manufacturer's restocking schedule. In this case, tracking will show a "Preparing for Shipment" status.

For such products, we ship them sequentially as soon as stock is replenished.

We will do our best to ensure smooth delivery even if there are delays.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 72,
      createdAt: '2024-08-27T00:00:00.000Z',
      updatedAt: '2024-08-27T00:00:00.000Z'
    },
    {
      id: '6',
      title: 'What is the overall shipping process?',
      content: `Order statuses progress in the following sequence:
Payment Pending > Preparing for Shipment > In Transit > Delivered
(Check order status at: Login > My Page > Order History)

Payment Pending
- We are waiting for payment confirmation. Your order will be processed immediately after confirmation.

Preparing for Shipment
- Your order has been confirmed and is being prepared before a tracking number is assigned.
- Orders are typically placed with our local partner within 1-2 business days.
- Considering customs and overall logistics, shipment is usually completed within 3-10 days.

In Transit
- Your product has been dispatched from our local logistics office.
- You can track your shipment using the tracking button on your order page.

* For any questions, please contact us via our support center or inquiry form, or email support@liberacure.com. We will provide prompt and courteous assistance.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 184,
      createdAt: '2024-08-29T00:00:00.000Z',
      updatedAt: '2024-08-29T00:00:00.000Z'
    },
    {
      id: '7',
      title: 'Can customs issues occur?',
      content: `Generally, there are no customs issues, so there is no need to worry.

Even if a customs issue does occur, there is no penalty to the individual.

If a customs issue occurs once, please contact us and we will reship immediately. If it occurs twice, we will reship or provide a full refund.`,
      author: 'LiberaCure',
      isImportant: false,
      views: 56,
      createdAt: '2024-09-02T00:00:00.000Z',
      updatedAt: '2024-09-02T00:00:00.000Z'
    }
  ];
}

export default AdminContext;

